import { Input } from "@twilio-paste/core/input";
import { Label } from "@twilio-paste/core/label";
import { HelpText } from "@twilio-paste/core/help-text";
import { Box } from "@twilio-paste/core/box";
import { FormEvent } from "react";
import { Button } from "@twilio-paste/core/button";
import { useDispatch, useSelector } from "react-redux";
import { Text } from "@twilio-paste/core/text";
import { ArrowBackIcon } from "@twilio-paste/icons/esm/ArrowBackIcon";

import { sessionDataHandler } from "../../sessionDataHandler";
import {
    addNotification,
    changeEngagementPhase,
    updatePreEngagementData,
    updatePreEngagementStep
} from "../../store/actions/genericActions";
import { initSession } from "../../store/actions/initActions";
import { reportClientError } from "../../utils/reportClientError";
import { extrairCodigos } from "../../utils/diagnosticoDeEnvio";
import { descreverErro } from "../../utils/descreverErro";
import { AppDispatch } from "../../store/store";
import { AppState, EngagementPhase, PrechatField } from "../../store/definitions";
import { notifications } from "../../notifications";
import { getFieldBehavior } from "./fieldBehaviors";
import {
    fieldStyles,
    formStyles,
    buttonContainerStyles,
    backButtonStyles
} from "../styles/PreEngagementFormPhase.styles";

/*
 * Default = Nome + CPF de hoje. `friendlyName`/`cpf` são as chaves de CONTRATO do submit
 * (viram attributes da conversa → pre_engagement_data → TaskRouter). Não renomear/remover.
 */
const DEFAULT_FIELDS: PrechatField[] = [
    { key: "friendlyName", label: "Nome", placeholder: "Digite seu nome", required: true, type: "text" },
    { key: "cpf", label: "CPF", placeholder: "Digite seu CPF", required: true, type: "cpf" }
];

/* Step 2: formulário dinâmico (campos vêm do prechat.form ou do default) + validação + submit. */
export const PrechatForm = () => {
    const dispatch = useDispatch<AppDispatch>();
    const preEngagementData = useSelector((state: AppState) => state.session.preEngagementData) || {};
    const configuredFields = useSelector((state: AppState) => state.config?.prechat?.form?.fields);

    const fields = configuredFields ?? DEFAULT_FIELDS;
    const valueOf = (key: string) => preEngagementData[key] ?? "";

    const isFormValid = fields.every((field) =>
        getFieldBehavior(field.type).isValid(valueOf(field.key), field.required)
    );

    const handleFieldChange = (field: PrechatField, raw: string) => {
        const value = getFieldBehavior(field.type).mask(raw);
        dispatch(updatePreEngagementData({ [field.key]: value }));
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        if (!isFormValid) {
            return;
        }
        dispatch(changeEngagementPhase({ phase: EngagementPhase.Loading }));
        try {
            const formData: Record<string, string> = {};
            fields.forEach((field) => {
                formData[field.key] = valueOf(field.key);
            });
            const data = await sessionDataHandler.fetchAndStoreNewSession({ formData });
            /*
             * O `await` aqui é obrigatório: `initSession` é um thunk ASSÍNCRONO e sem ele a rejeição
             * escapava deste `catch` — a fase já tinha virado `Loading` acima, então o apostador
             * ficava no spinner para sempre, sem aviso. Falha silenciosa.
             */
            await dispatch(initSession({ token: data.token, conversationSid: data.conversationSid }));
        } catch (err) {
            // motivo técnico só na telemetria; a tela leva mensagem genérica (nada de texto do SDK)
            /*
             * `descreverErro` e NÃO `err.message` cru (gate de compliance, 2026-08-28): este é o
             * componente que coleta NOME e CPF, e a mensagem de erro pode passar a carregar o campo
             * recusado no dia em que o backend mudar. O motivo nasce normalizado (nome + código,
             * mensagem limitada) em vez de confiar na origem.
             */
            reportClientError("session_init_failed", { reason: descreverErro(err), ...extrairCodigos(err) });
            dispatch(addNotification(notifications.failedToInitSessionNotification()));
            dispatch(changeEngagementPhase({ phase: EngagementPhase.PreEngagementForm }));
        }
    };

    return (
        <Box as="form" data-test="pre-engagement-chat-form" onSubmit={handleSubmit} {...formStyles}>
            {fields.map((field) => {
                const behavior = getFieldBehavior(field.type);
                const showError = behavior.showError(valueOf(field.key));
                return (
                    <Box {...fieldStyles} key={field.key}>
                        <Label htmlFor={field.key}>{field.label}</Label>
                        <Input
                            type="text"
                            placeholder={field.placeholder}
                            name={field.key}
                            data-test={`pre-engagement-chat-form-${field.key}-input`}
                            value={valueOf(field.key)}
                            onChange={(e) => handleFieldChange(field, e.target.value)}
                            hasError={behavior.errorText ? showError : undefined}
                            inputMode={behavior.inputMode}
                            maxLength={behavior.maxLength}
                            aria-describedby={behavior.errorText ? `${field.key}-error` : undefined}
                            required={field.required}
                        />
                        {showError && behavior.errorText && (
                            <HelpText id={`${field.key}-error`} variant="error">
                                {behavior.errorText}
                            </HelpText>
                        )}
                    </Box>
                );
            })}
            <Box {...buttonContainerStyles}>
                <Box
                    as="button"
                    type="button"
                    data-test="pre-engagement-back-button"
                    onClick={() => dispatch(updatePreEngagementStep(1))}
                    {...backButtonStyles}
                >
                    <ArrowBackIcon decorative={true} size="sizeIcon30" />
                    <Text as="span" marginLeft="space20">
                        Voltar
                    </Text>
                </Box>
                <Button
                    variant="primary"
                    type="submit"
                    data-test="pre-engagement-start-chat-button"
                    disabled={!isFormValid}
                >
                    Iniciar conversa
                </Button>
            </Box>
        </Box>
    );
};
